import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styles from './CertainDayPricesCard.module.scss';
import {
  getCorrectPrice,
  getErrorText,
  setFieldErrorText,
} from '../../utils/functions';
import {
  ButtonType,
  FieldTypes,
  InputType,
  OptionsListType,
} from '../../utils/@globalTypes';
import Button from '../Button';
import { CalendarIcon2, DeleteIcon, EditIcon } from '../../assets/icons';
import Input from '../Input';
import Calendar from '../Calendar';
import { format, addMonths, subDays } from 'date-fns';
import {
  CertainDayPriceStatus,
  ResponseCertainDayPriceType,
  ResponseCertainDayPriceTypeList,
  UpdateCertainDayPricePayloadData,
} from '../../redux/types/pricesTypes';
import { useDispatch, useSelector } from 'react-redux';
import {
  createCertainPrice,
  removeCertainDayPrice,
  removeCertainDayPricesStatus,
  setCertainDayPricesStatus,
  updateCertainDayPrice,
} from '../../redux/reducers/pricesSlice';
import { formatDate } from '../Booking/functions';
import classNames from 'classnames';
import Loader from '../Loader';
import {
  AuthSelectors,
  setErrorResponeData,
} from '../../redux/reducers/authSlice';
import { priceMask } from '../../utils/constants';
import SelectComponentV2 from '../SelectComponentV2';
import { RentalObjectSelectors } from '../../redux/reducers/rentalObjectSlice';

type CertainDayPricesCardProps = {
  rentalId: number;
  data: ResponseCertainDayPriceType;
  status: CertainDayPriceStatus;
  setCertainDayPricesList: React.Dispatch<
    React.SetStateAction<ResponseCertainDayPriceTypeList>
  >;
  isPriceDynamic?: boolean;
};

const CertainDayPricesCard: FC<CertainDayPricesCardProps> = ({
  data,
  status,
  setCertainDayPricesList,
  rentalId,
  isPriceDynamic = true,
}) => {
  const dispatch = useDispatch();
  const formRef = useRef<HTMLDivElement>(null);
  const errorResponseData = useSelector(AuthSelectors.getErrorResponseData);

  const currentRentalObject = useSelector(
    RentalObjectSelectors.getCurrentRentalObject,
  );
  const max_places = currentRentalObject?.max_places || 0;
  let minGuestOptions: OptionsListType = [];
  for (let i = 1; i <= max_places; i++) {
    minGuestOptions.push({ value: i.toString(), label: i.toString() });
  }
  const isLast: boolean =
    new Date(data.end_date) < subDays(new Date(), 1) ? true : false;

  const initialExtraPrice = data.extra_guest_price ? data.extra_guest_price : ""

  const [price, setPrice] = useState(data.base_price);
  const [addPrice, setAddPrice] = useState(initialExtraPrice)
 
  const [priceError, setPriceError] = useState('');
  const [addPriceError, setAddPriceError] = useState('');
  const [dateError, setDateError] = useState('');

  const [priceTouched, setPriceTouched] = useState(false);
  const [addPriceTouched, setAddPriceTouched] = useState(false);
  const [dateTouched, setDateTouched] = useState(false);

  const initialMinGuests = status.isNew
    ? isPriceDynamic
      ? '1'
      : Number(max_places).toString()
    : data.min_guests !== null ? data.min_guests.toString() : max_places.toString();

  const [minGuests, setMinGuests] = useState<string>(initialMinGuests);
    
  const [minGuestsError, setMinGuestsError] = useState<string>('');
  const [isEdit, setIsEdit] = useState(false);

  const today = new Date();
  const minDate = today;
  const maxDate = addMonths(today, 15);
  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([
    data.start_date ? new Date(data.start_date) : undefined,
    data.end_date ? new Date(data.end_date) : undefined,
  ]);
  const [startDate, endDate] = dateRange;
  const placeholder = `${format(minDate, 'dd.MM.yyyy')} - ${format(maxDate, 'dd.MM.yyyy')}`;
  const date =
    startDate &&
    endDate &&
    `${format(startDate, 'dd.MM.yyyy')} - ${format(endDate, 'dd.MM.yyyy')}`;

  const clearCalendar = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    setDateRange([undefined, undefined]);
  };

  useEffect(() => {
    if (!errorResponseData && isEdit && dateError) {
      setDateError('');
    }
  }, [isEdit]);

  useEffect(() => {
    if (status.isNew && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const onRemoveCertainDayPriceClick = () => {
    if (status.isNew) {
      setCertainDayPricesList((prevState) =>
        prevState.filter((item) => item.id !== data.id),
      );
      dispatch(removeCertainDayPricesStatus(data.id));
    } else {
      dispatch(removeCertainDayPrice(data.id));
    }
  };

  const onToggleIsEditCertainDayPriceClick = () => {
    setPrice(data.base_price);
    setDateRange([
      data.start_date ? new Date(data.start_date) : undefined,
      data.end_date ? new Date(data.end_date) : undefined,
    ]);
    setIsEdit(!isEdit);
  };

  const onSaveCertainDayPriceClick = () => {
    setDateTouched(true);
    setPriceTouched(true);
    setAddPriceTouched(true);
    if (
      startDate &&
      endDate &&
      price &&
      !priceError &&
      !dateError &&
      !addPriceError
    ) {
      if (status.isNew) {
        dispatch(
          createCertainPrice({
            id: data.id,
            data: {
              start_date: formatDate(startDate),
              end_date: formatDate(endDate),
              base_price: price,
              extra_guest_price: addPrice || '0',
              rental_object: rentalId,
              min_guests: minGuests,
            },
          }),
        );
      } else {
        const payloadData: UpdateCertainDayPricePayloadData = {};

        const {
          start_date,
          end_date,
          base_price: old_price,
          extra_guest_price: old_extra,
          min_guests: old_guests,
        } = data;

        start_date !== formatDate(startDate) &&
          (payloadData.start_date = formatDate(startDate));
        end_date !== formatDate(endDate) &&
          (payloadData.end_date = formatDate(endDate));
        +old_price !== +price && (payloadData.base_price = price);
        Number(old_extra) !== Number(addPrice) && (payloadData.extra_guest_price = addPrice);
        +old_guests !== +minGuests && (payloadData.min_guests = minGuests);
        dispatch(
          updateCertainDayPrice({
            id: data.id,
            data: payloadData,
          }),
        );
      }
    }
  };

  useEffect(() => {
    if (errorResponseData) {
      errorResponseData.start_date &&
        setDateError(
          getErrorText(
            errorResponseData.start_date[0],
            '',
            FieldTypes.CERTAIN_DAY_PRICE,
          ),
        );

      dispatch(setErrorResponeData(null));
    }
  }, [errorResponseData]);

  useEffect(() => {
    if (status.isSuccess) {
      if (status.isNew) {
        setCertainDayPricesList((prevState) =>
          prevState.filter((item) => item.id !== data.id),
        );
      } else {
        setIsEdit(!isEdit);
        dispatch(setCertainDayPricesStatus({ ...status, isSuccess: false }));
      }
    }
  }, [status, dispatch, isEdit]);

  useEffect(() => {
    if (dateTouched) {
      if (!startDate || !endDate) {
        setDateError('Обязательное поле');
      } else {
        setDateError('');
      }
    }
  }, [dateTouched, startDate, endDate]);

  useEffect(() => {
    setFieldErrorText(
      priceTouched,
      price,
      setPriceError,
      FieldTypes.CERTAIN_DAY_PRICE,
      false,
    );
  }, [priceTouched, price]);

  useEffect(() => {
    if (!max_places) {
      setMinGuestsError(
        'Заполните максимальное количество мест на вкладке "Базовые настройки"',
      );
    } else if (max_places < Number(minGuests)) {
      setMinGuestsError(
        'Проверьте максимальную вместимость объекта и измените данные по количеству гостей при необходимости',
      );
    } else {
      setMinGuestsError('');
    }
  }, [max_places, minGuests]);

  // useEffect(() => {
  //   if (addPriceTouched) {
  //     if (!addPrice && +minGuests < +max_places) {
  //       setAddPriceError(
  //         'Введите цену за дополнительного гостя или измените минимальное количество гостей до максимально возможного',
  //       );
  //     } else {
  //       setAddPriceError('');
  //     }
  //   }
  // }, [addPriceTouched, addPrice, minGuests]);

  const isValid = useMemo(() => {
    return !priceError && !dateError && !minGuestsError;
  }, [priceError, dateError, minGuestsError]);

  const isFieldsChanged = useMemo(() => {
    if (startDate && endDate) {
      const {
        start_date,
        end_date,
        base_price: old_price,
        extra_guest_price: old_addPrice,
        min_guests: old_guests,
      } = data;
      return (
        start_date !== formatDate(startDate) ||
        end_date !== formatDate(endDate) ||
        +old_price !== +price ||
        +old_addPrice !== +addPrice ||
        +minGuests !== +old_guests
      );
    }
  }, [startDate, endDate, price, data, minGuests, addPrice]);

  return (
    <div
      ref={formRef}
      className={classNames(styles.wrapper, {
        [styles.editMode]: status.isNew || isEdit,
        [styles.viewMode]: !isEdit && !status.isNew,
        [styles.last]: isLast,
      })}
    >
      {status.isLoading && (
        <div className={styles.loaderOverlay}>
          <Loader size={50} />
        </div>
      )}
      <div className={styles.inputWrapper}>
        {isEdit || status.isNew ? (
          <>
            <div className={styles.cardItem}>
              <p className={styles.title}>Период*</p>
              <Calendar
                selectedDate={today}
                maxDate={maxDate}
                minDate={minDate}
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => {
                  setDateRange(update);
                }}
                onBlur={setDateTouched}
                inputClassname={classNames(styles.calendar, {
                  [styles.errorInput]: dateError,
                })}
                wrapperInputClassname={styles.cardItem}
                placeholderText={placeholder}
                clearCalendar={clearCalendar}
              />
              {dateError && <p className={styles.errorText}>{dateError}</p>}
            </div>
            <Input
              value={price}
              title="Базовая цена*"
              placeholder="0.00"
              type={InputType.MASK}
              onChange={setPrice}
              errText={priceError}
              onBlur={setPriceTouched}
              mask={priceMask}
              // containerClassName={styles.cardItem}
              containerClassName="price"
            />
            <Input
              value={addPrice}
              title="Дополнительный гость"
              placeholder="0.00"
              type={InputType.MASK}
              onChange={setAddPrice}
              errText={addPriceError}
              onBlur={setAddPriceTouched}
              mask={priceMask}
              // containerClassName={styles.cardItem}
              containerClassName="price"
            />
            <div className={styles.count_guests}>
              <div className={styles.lineTitle}>
                Минимальное количество гостей
              </div>
              <SelectComponentV2
                title="человек"
                placeholder="-"
                // defaultValueId={0}
                optionsList={minGuestOptions}
                currentValue={minGuests.toString()}
                className={styles.guest}
                setSelecValue={setMinGuests}
                noOptionsMessage="Нет данных"
                errText={minGuestsError}
              />
            </div>
          </>
        ) : (
          <div className={styles.cardInfo}>
            <div className={classNames(styles.cardItem, styles.dates)}>
              <p className={classNames(styles.viewTitle, styles.period)}>
                Период:
              </p>
              <p className={styles.dateBlock}>
                <CalendarIcon2 />
                {date}
              </p>
            </div>

            <div className={classNames(styles.cardItem, styles.basePrice)}>
              <p className={styles.viewTitle}>Базовая цена:</p>
              <p className={styles.priceBlock}>{getCorrectPrice(price)}</p>
            </div>

            <div className={classNames(styles.cardItem, styles.addPrice)}>
              <p className={styles.viewTitle}>Дополнительный гость:</p>
              <p className={styles.priceBlock}>{getCorrectPrice(addPrice)}</p>
            </div>

            <div className={classNames(styles.cardItem, styles.guests)}>
              <p className={styles.viewTitle}>Минимальное количество гостей:</p>
              <p className={styles.priceBlock}>{minGuests}</p>
            </div>
            {!isLast && minGuestsError && (
              <div className={styles.errorText}>{minGuestsError}</div>
            )}
          </div>
        )}
      </div>

      <div
        className={classNames(styles.controls, {
          [styles.hiddenControls]: isLast,
        })}
      >
        {(isEdit || status.isNew) && (
          <Button
            title="Сохранить"
            type={ButtonType.PRIMARY_SMALL}
            onClick={onSaveCertainDayPriceClick}
            disabled={!isValid || !isFieldsChanged || !max_places || !price}
            wrapperClassName={
              isEdit || status.isNew ? styles.saveBtnWrap : styles.viewBtnWrap
            }
            className={isEdit || status.isNew ? styles.saveBtn : styles.viewBtn}
          />
        )}
        {!status.isNew && (
          <Button
            title={isEdit ? 'Отмена' : <EditIcon />}
            type={ButtonType.SECONDARY_SMALL}
            onClick={onToggleIsEditCertainDayPriceClick}
            wrapperClassName={isEdit ? styles.cancelBtnWrap : undefined}
            className={isEdit ? styles.cancelBtn : undefined}
          />
        )}
        <Button
          title={<DeleteIcon />}
          type={ButtonType.SECONDARY_SMALL}
          onClick={onRemoveCertainDayPriceClick}
          confirmWindowText={
            !status.isNew ? 'Действительно хотите удалить?' : undefined
          }
          windowClassName="bookingConfirm"
        />
      </div>
    </div>
  );
};

export default CertainDayPricesCard;
